import { Link } from "react-router-dom";
import { ArrowRight, Users, Briefcase, Code2, Cog, Cpu, Database, ShieldCheck, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import heroImage from "@/assets/hero-home.jpg";

const capabilities = [
  { icon: Code2, title: "Product Engineering" },
  { icon: Cog, title: "Digital Transformation" },
  { icon: Briefcase, title: "Application Development" },
  { icon: ShieldCheck, title: "DevOps & QA" },
  { icon: Layers, title: "SAP Services" },
  { icon: Cpu, title: "AI / ML" },
  { icon: Database, title: "Data Analytics" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 mm-grid-bg pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-24 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="mm-fade-up inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-orange-200 bg-orange-50 text-xs tracking-wider uppercase text-orange-700">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
                Talent. Delivered.
              </div>

              <h1 className="mm-fade-up mm-delay-1 mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light tracking-tight text-slate-900 leading-[1.05]">
                The right engineers,<br />
                <span className="font-medium">when you need them.</span>
              </h1>

              <p className="mm-fade-up mm-delay-2 mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
                T3 Solutions brings elite contract, remote, and full-time talent to power your product engineering, digital transformation, AI/ML, and data initiatives.
              </p>

              <div className="mm-fade-up mm-delay-3 mt-10 flex flex-col sm:flex-row gap-3">
                <Button
                  asChild
                  data-testid="home-hero-cta-services"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-md h-12 px-7 text-sm font-semibold tracking-wide"
                >
                  <Link to="/services">
                    View Services <ArrowRight className="ml-2" size={16} />
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  data-testid="home-hero-cta-looking-for"
                  className="border-slate-300 text-slate-900 hover:bg-slate-50 rounded-md h-12 px-7 text-sm font-semibold tracking-wide"
                >
                  <Link to="/looking-for">What You're Looking For</Link>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-5 mm-fade-up mm-delay-3">
              <div className="relative">
                <div className="absolute -inset-4 bg-slate-100 rounded-2xl -z-10" aria-hidden="true" />
                <img
                  data-testid="home-hero-image"
                  src={heroImage}
                  alt="A modern tech team collaborating"
                  className="w-full h-[480px] object-cover rounded-xl border border-slate-200"
                  loading="eager"
                />
                <div className="absolute -bottom-6 -left-6 bg-white border border-slate-200 rounded-lg p-4 shadow-sm max-w-[240px] hidden sm:block">
                  <div className="flex items-center gap-2 mb-2">
                    <Users size={16} className="text-orange-500" />
                    <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">Talent network</span>
                  </div>
                  <p className="text-sm text-slate-700 leading-snug">
                    Engineers, architects, and specialists ready to embed with your teams.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="text-xs uppercase tracking-[0.18em] text-orange-600 font-semibold mb-3">
                What we deliver
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-medium text-slate-900 tracking-tight">
                Specialists across the engineering spectrum.
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed text-sm sm:text-base max-w-md">
                From greenfield builds to large-scale modernization — our network covers the disciplines that move modern technology forward.
              </p>
            </div>
            <div className="lg:col-span-8">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-lg overflow-hidden">
                {capabilities.map(({ icon: Icon, title }) => (
                  <div
                    key={title}
                    data-testid={`capability-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="bg-white p-6 sm:p-8 flex flex-col gap-4 hover:bg-slate-50 transition-colors"
                  >
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-blue-600 text-white">
                      <Icon size={18} />
                    </span>
                    <div className="font-heading text-base sm:text-lg font-medium text-slate-900 leading-snug">
                      {title}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="border border-slate-200 rounded-xl p-10 lg:p-16 bg-white relative overflow-hidden">
          <div className="absolute inset-0 mm-grid-bg opacity-60 pointer-events-none" aria-hidden="true" />
          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="text-xs uppercase tracking-[0.18em] text-orange-600 font-semibold mb-3">
                Ready to scale?
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-slate-900 tracking-tight max-w-2xl">
                Tell us what you need.<br />
                <span className="font-medium">We'll bring the talent.</span>
              </h2>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Button
                asChild
                data-testid="home-cta-bottom-looking-for"
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-md h-12 px-8 text-sm font-semibold tracking-wide"
              >
                <Link to="/looking-for">
                  Start a request <ArrowRight className="ml-2" size={16} />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
